import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MapContainer, Marker, Popup, TileLayer, useMap } from "react-leaflet";
import L from "leaflet";
import { Search, Phone, MessageCircle, Filter, MapPinned, ArrowRight } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";

import { siteConfig } from "@/config/siteConfig";
import { fetchCoverageLocations } from "@/services/coverageService";
import { coverageUtils, coverageStatusListAR, coverageStatusListEN } from "@/utils/coverageUtils";
import { COVERAGE } from "@/constants/testIds";

const twinLogo = "/assets/logo/twin-tc-logo-transparent.png";

const markerIcon = (color) =>
  L.divIcon({
    className: "coverage-marker-icon",
    html: `<span style="display:inline-block;width:14px;height:14px;border-radius:999px;background:${color};box-shadow:0 0 0 3px rgba(255,255,255,0.18),0 0 14px ${color};"></span>`,
    iconSize: [16, 16],
    iconAnchor: [8, 8],
  });

const FlyToLocation = ({ target }) => {
  const map = useMap();
  React.useEffect(() => {
    if (target) {
      map.flyTo([target.latitude, target.longitude], 12, { duration: 0.9 });
    }
  }, [map, target]);
  return null;
};

const CoveragePopupActions = ({ location, lang }) => {
  const statusMeta = coverageUtils.getCoverageStatusMeta(location.service_status, lang);
  const locationName = lang === "ar" ? location.region_name_ar : location.region_name_en;
  const waMessage = coverageUtils.formatCoverageWhatsappMessage({
    locationName,
    statusLabel: statusMeta.label,
    lang,
  });
  const whatsappDigits = (location.whatsapp || siteConfig.contact.whatsapp).replace(/\D/g, "");
  const requestWhatsappDigits = siteConfig.contact.whatsapp.replace(/\D/g, "");
  const phoneDigits = (location.phone || siteConfig.contact.phone).replace(/\D/g, "");

  return (
    <div className="mt-3 flex flex-wrap gap-2">
      <a
        href={`https://wa.me/${whatsappDigits}?text=${waMessage}`}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-1 rounded-lg border border-[#2ef2a5]/35 bg-[#2ef2a5]/20 px-2.5 py-1.5 text-xs"
        data-testid={COVERAGE.mapPopupWhatsAppButton}
      >
        <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
      </a>
      <a
        href={`tel:${phoneDigits}`}
        className="inline-flex items-center gap-1 rounded-lg border border-white/25 bg-white/10 px-2.5 py-1.5 text-xs"
        data-testid={COVERAGE.mapPopupCallButton}
      >
        <Phone className="h-3.5 w-3.5" /> Call
      </a>
      <a
        href={`https://wa.me/${requestWhatsappDigits}?text=${waMessage}`}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-1 rounded-lg border border-primary/35 bg-primary/20 px-2.5 py-1.5 text-xs"
        data-testid={COVERAGE.mapPopupRequestButton}
      >
        <MapPinned className="h-3.5 w-3.5" /> {lang === "ar" ? "طلب الخدمة" : "Request Service"}
      </a>
    </div>
  );
};

export const CoverageMapPage = () => {
  const [lang, setLang] = useState("ar");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [loading, setLoading] = useState(false);

  const statusOptions = lang === "ar" ? coverageStatusListAR : coverageStatusListEN;

  const loadLocations = async () => {
    setLoading(true);
    try {
      const data = await fetchCoverageLocations({
        search,
        status: statusFilter,
        lang,
      });
      setLocations(data.items || []);
      if (!selectedLocation && data.items?.length) {
        setSelectedLocation(data.items[0]);
      }
    } catch (error) {
      toast.error(lang === "ar" ? "تعذر تحميل بيانات الخريطة" : "Failed to load map data");
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    loadLocations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang, statusFilter]);

  const filteredLocations = useMemo(() => {
    if (!search.trim()) {
      return locations;
    }
    const query = search.toLowerCase();
    return locations.filter((location) =>
      [location.region_name_ar, location.region_name_en, location.city]
        .join(" ")
        .toLowerCase()
        .includes(query),
    );
  }, [locations, search]);

  const defaultCenter = [33.5138, 36.2765];

  return (
    <div className={`min-h-screen bg-background text-foreground ${lang === "ar" ? "font-ar" : "font-en"}`} dir={lang === "ar" ? "rtl" : "ltr"}>
      <header className="coverage-topbar sticky top-0 z-40">
        <div className="container-shell flex min-h-[68px] items-center justify-between gap-4 py-3">
          <Link to="/" className="inline-flex items-center gap-3">
            <img src={twinLogo} alt="TWIN TC logo" className="brand-logo logo-glow h-12 w-auto sm:h-14" />
          </Link>
          <div className="flex items-center gap-2">
            <Button variant="outline" className="rounded-xl border-white/20 bg-card/70 px-3" onClick={() => setLang("ar")}>
              AR
            </Button>
            <Button variant="outline" className="rounded-xl border-white/20 bg-card/70 px-3" onClick={() => setLang("en")}>
              EN
            </Button>
            <Button asChild className="rounded-xl bg-primary text-primary-foreground">
              <Link to="/admin/coverage">{lang === "ar" ? "إدارة الخريطة" : "Coverage Admin"}</Link>
            </Button>
          </div>
        </div>
      </header>

      <main className="container-shell py-8">
        <div className="mb-6 grid gap-4 lg:grid-cols-[1fr_auto]">
          <div>
            <h1 className="text-3xl font-bold sm:text-4xl">{lang === "ar" ? "خريطة التغطية" : "Coverage Map"}</h1>
            <p className="mt-3 max-w-3xl text-sm leading-8 text-muted-foreground sm:text-base">
              {lang === "ar"
                ? "خريطة تفاعلية لحالة الخدمة حسب المناطق. البيانات الحالية هي Sample Data / بيانات تجريبية قابلة للتعديل من لوحة الإدارة."
                : "Interactive service availability map by region. Current records are Sample Data editable from admin panel."}
            </p>
          </div>
          <div className="flex flex-wrap gap-2 lg:justify-end">
            <a
              href={`https://wa.me/${siteConfig.contact.whatsapp.replace(/\D/g, "")}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex h-11 items-center gap-2 rounded-xl border border-[#2ef2a5]/35 bg-[#2ef2a5]/20 px-4 text-sm"
            >
              <MessageCircle className="h-4 w-4" /> WhatsApp
            </a>
            <a
              href={`tel:${siteConfig.contact.phone.replace(/\D/g, "")}`}
              className="inline-flex h-11 items-center gap-2 rounded-xl border border-white/20 bg-card/80 px-4 text-sm"
            >
              <Phone className="h-4 w-4" /> {lang === "ar" ? "اتصال" : "Call"}
            </a>
            <a
              href="/#support"
              className="inline-flex h-11 items-center gap-2 rounded-xl border border-primary/30 bg-primary/20 px-4 text-sm"
            >
              <ArrowRight className="h-4 w-4" /> {lang === "ar" ? "نموذج التواصل" : "Contact Form"}
            </a>
          </div>
        </div>

        <div className="mb-5 grid gap-3 md:grid-cols-[1fr_280px_auto]">
          <div className="relative">
            <Search className="pointer-events-none absolute top-1/2 -translate-y-1/2 start-3 h-4 w-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={lang === "ar" ? "ابحث باسم المنطقة أو المدينة" : "Search by region or city"}
              className="ps-9"
              data-testid={COVERAGE.mapSearchInput}
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid={COVERAGE.mapStatusFilter}>
              <Filter className="me-2 h-4 w-4" />
              <SelectValue placeholder={lang === "ar" ? "كل الحالات" : "All statuses"} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{lang === "ar" ? "كل الحالات" : "All statuses"}</SelectItem>
              {statusOptions.map((status) => (
                <SelectItem key={status.value} value={status.value}>
                  {status.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button className="rounded-xl bg-primary text-primary-foreground" onClick={loadLocations}>
            {loading ? (lang === "ar" ? "جاري التحميل..." : "Loading...") : lang === "ar" ? "تحديث النتائج" : "Refresh"}
          </Button>
        </div>

        <div className="grid gap-4 xl:grid-cols-[340px_1fr]">
          <Card className="glass-card border-white/12">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">{lang === "ar" ? "قائمة المناطق" : "Coverage Areas"}</CardTitle>
              <CardDescription>
                {lang === "ar"
                  ? "اضغط على أي منطقة للانتقال إليها على الخريطة"
                  : "Select any location to focus it on the map"}
              </CardDescription>
            </CardHeader>
            <CardContent className="max-h-[620px] space-y-3 overflow-auto">
              {filteredLocations.map((location) => {
                const statusMeta = coverageUtils.getCoverageStatusMeta(location.service_status, lang);
                const name = lang === "ar" ? location.region_name_ar : location.region_name_en;

                return (
                  <button
                    key={location.id}
                    type="button"
                    className="w-full rounded-xl border border-white/10 bg-[#0e152a] p-3 text-start hover:border-primary/40"
                    onClick={() => setSelectedLocation(location)}
                    data-testid={COVERAGE.mapSidebarItem}
                  >
                    <div className="mb-2 flex items-center justify-between gap-3">
                      <p className="text-sm font-semibold">{name}</p>
                      <Badge style={{ backgroundColor: `${statusMeta.color}25`, color: statusMeta.color, borderColor: `${statusMeta.color}66` }}>
                        {statusMeta.label}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">{location.city}</p>
                  </button>
                );
              })}

              {filteredLocations.length === 0 ? (
                <p className="text-sm text-muted-foreground">{lang === "ar" ? "لا توجد نتائج" : "No locations found"}</p>
              ) : null}
            </CardContent>
          </Card>

          <Card className="glass-card border-white/12">
            <CardContent className="p-3">
              <div className="coverage-map-wrapper" data-testid={COVERAGE.mapContainer}>
                <MapContainer center={defaultCenter} zoom={7} className="h-[620px] w-full rounded-xl" scrollWheelZoom>
                  <TileLayer
                    attribution='&copy; OpenStreetMap contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <FlyToLocation target={selectedLocation} />
                  {filteredLocations.map((location) => {
                    const statusMeta = coverageUtils.getCoverageStatusMeta(location.service_status, lang);
                    const name = lang === "ar" ? location.region_name_ar : location.region_name_en;
                    const description = lang === "ar" ? location.description_ar : location.description_en;

                    return (
                      <Marker
                        key={location.id}
                        position={[location.latitude, location.longitude]}
                        icon={markerIcon(statusMeta.color)}
                        eventHandlers={{
                          click: () => setSelectedLocation(location),
                        }}
                      >
                        <Popup minWidth={260}>
                          <div className="space-y-2">
                            <p className="text-sm font-semibold">{name}</p>
                            <Badge style={{ backgroundColor: `${statusMeta.color}25`, color: statusMeta.color, borderColor: `${statusMeta.color}66` }}>
                              {statusMeta.label}
                            </Badge>
                            <p className="text-xs text-slate-600">{description}</p>
                            <CoveragePopupActions location={location} lang={lang} />
                          </div>
                        </Popup>
                      </Marker>
                    );
                  })}
                </MapContainer>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};
